import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { authApi, api, TOKEN_KEY, USER_KEY } from "@/services/api";

const AuthContext = createContext(undefined);

const IMP_KEY = "fdc_impersonator";
const STAFF_ROLES = ["inspector", "engineer", "admin"];

export function isStaff(u) {
  if (!u) return false;
  if (u.is_company_admin || u.is_platform_admin) return true;
  if (STAFF_ROLES.includes(u.role)) return true;
  return (u.secondary_roles || []).some((r) => STAFF_ROLES.includes(r));
}

export function isClient(u) {
  return !!u && u.role === "client";
}

export function isContractor(u) {
  return !!u && u.role === "contractor";
}

export function isEngineer(u) {
  return userHasRole(u, "engineer");
}

export function isInspectorLike(u) {
  if (!u) return false;
  if (u.is_company_admin || u.is_platform_admin) return true;
  return userHasRole(u, "inspector", "admin");
}

// A staff member who is BOTH an engineer and an inspector/admin. These
// dual-role users get the engineer-vs-inspector mode chooser after login.
export function isDualStaff(u) {
  return isEngineer(u) && isInspectorLike(u);
}

// Who may switch between Inspector and Engineer mode: dual-role staff
// (engineer + inspector) AND any company/platform admin. Admins can work
// in either mode even without an explicit engineer role.
export function canSwitchMode(u) {
  if (!u) return false;
  if (isDualStaff(u)) return true;
  return !!(u.is_company_admin || u.is_platform_admin);
}

export function homePathFor(u) {
  if (isContractor(u)) return "/partner";
  if (isClient(u)) return "/portal";
  if (isEngineer(u) && !isInspectorLike(u)) return "/engineer"; // pure engineer
  if (isDualStaff(u)) return "/mode"; // dual-role → pick a mode
  return "/dashboard"; // admins default to the inspector console (they can switch via the menu)
}

export function userHasRole(u, ...wanted) {
  if (!u || wanted.length === 0) return false;
  if (u.role && wanted.includes(u.role)) return true;
  return (u.secondary_roles || []).some((r) => wanted.includes(r));
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(() => {
    try { return JSON.parse(localStorage.getItem(IMP_KEY) || "null"); } catch { return null; }
  });

  useEffect(() => {
    (async () => {
      try {
        const storedToken = localStorage.getItem(TOKEN_KEY);
        const storedUser = localStorage.getItem(USER_KEY);
        if (storedToken && storedUser) {
          setToken(storedToken);
          try {
            setUser(JSON.parse(storedUser));
          } catch {
            /* ignore malformed */
          }
          // Validate silently in the background.
          try {
            const res = await authApi.me();
            setUser(res.data);
            localStorage.setItem(USER_KEY, JSON.stringify(res.data));
          } catch {
            /* interceptor handles a hard 401 */
          }
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const persist = (tok, usr) => {
    localStorage.setItem(TOKEN_KEY, tok);
    localStorage.setItem(USER_KEY, JSON.stringify(usr));
    setToken(tok);
    setUser(usr);
  };

  const login = useCallback(async (email, password, opts = {}) => {
    const res = await authApi.login(email.trim().toLowerCase(), password);
    const { access_token, user: userData } = res.data;
    if (userData?.role === "client" && !opts.allowClient) {
      const err = new Error(
        "This is a client account. Please sign in via the Client Portal."
      );
      err.code = "CLIENT_ROLE_REJECTED";
      throw err;
    }
    if (userData?.role !== "client" && opts.clientOnly) {
      const err = new Error(
        "This is a staff account. Use the staff sign-in instead."
      );
      err.code = "STAFF_ROLE_REJECTED";
      throw err;
    }
    persist(access_token, userData);
    return userData;
  }, []);

  const registerCompany = useCallback(async (payload) => {
    const res = await authApi.registerCompany(payload);
    const { access_token, user: userData } = res.data;
    persist(access_token, userData);
    return userData;
  }, []);

  const register = useCallback(async (payload) => {
    const res = await authApi.register(payload);
    const { access_token, user: userData } = res.data;
    persist(access_token, userData);
    return userData;
  }, []);

  const clientRegister = useCallback(async (payload) => {
    const res = await authApi.clientRegister(payload);
    const { access_token, user: userData } = res.data;
    persist(access_token, userData);
    return userData;
  }, []);

  const registerContractor = useCallback(async (payload) => {
    const res = await authApi.registerContractor(payload);
    const { access_token, user: userData } = res.data;
    persist(access_token, userData);
    return userData;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null);
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const res = await authApi.me();
      setUser(res.data);
      localStorage.setItem(USER_KEY, JSON.stringify(res.data));
    } catch {
      /* ignore */
    }
  }, []);

  // Super-admin impersonation: stash the admin session, swap in the
  // impersonation token, and reload the app as the target user.
  const beginImpersonation = useCallback(async (accessToken, targetLabel) => {
    const adminToken = localStorage.getItem(TOKEN_KEY);
    const adminUser = localStorage.getItem(USER_KEY);
    localStorage.setItem(IMP_KEY, JSON.stringify({ adminToken, adminUser, label: targetLabel || "user" }));
    localStorage.setItem(TOKEN_KEY, accessToken);
    // Resolve the impersonated user before entering.
    const me = await api.get("/auth/me", { headers: { Authorization: `Bearer ${accessToken}` } });
    localStorage.setItem(USER_KEY, JSON.stringify(me.data));
    window.location.href = "/";
  }, []);

  const exitImpersonation = useCallback(() => {
    let imp = null;
    try { imp = JSON.parse(localStorage.getItem(IMP_KEY) || "null"); } catch { /* ignore */ }
    // Default to the platform console (super-admin flow). For a company
    // admin previewing a client we route back to their own home instead
    // — they have no /platform access.
    let dest = "/platform";
    if (imp?.adminToken) {
      localStorage.setItem(TOKEN_KEY, imp.adminToken);
      if (imp.adminUser) {
        localStorage.setItem(USER_KEY, imp.adminUser);
        try {
          const au = JSON.parse(imp.adminUser);
          dest = au?.is_platform_admin ? "/platform" : homePathFor(au);
        } catch { /* keep default */ }
      }
    }
    localStorage.removeItem(IMP_KEY);
    window.location.href = dest;
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, token, loading, impersonating, login, register, clientRegister, registerCompany, registerContractor, logout, refreshUser, beginImpersonation, exitImpersonation }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (ctx === undefined) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
